import * as React from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Link,
  Alert,
  Avatar,
  Card,
  CardContent,
  Chip,
  Stack,
  Divider,
  Grid,
  alpha,
  useTheme,
} from '@mui/material';
import CheckCircle from '@mui/icons-material/CheckCircle';
import LoginIcon from '@mui/icons-material/Login';
import Email from '@mui/icons-material/Email';
import Lock from '@mui/icons-material/Lock';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  saveAuthReturnRoute,
  isSafeInternalPath,
  toPathString,
  deriveLabel,
  getDashboardFallback,
} from '../services/authReturnService';

const getReturnRouteFromState = (value: unknown) => {
  if (!value || typeof value !== 'object') return null;
  const candidate = value as { pathname?: string; search?: string; hash?: string };
  const route = {
    pathname: typeof candidate.pathname === 'string' ? candidate.pathname : '',
    search: typeof candidate.search === 'string' ? candidate.search : '',
    hash: typeof candidate.hash === 'string' ? candidate.hash : '',
  };
  if (!isSafeInternalPath(route)) return null;
  return route;
};

const Login: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, signInWithProvider, isAuthenticated } = useAuth();
  const [formData, setFormData] = React.useState({
    email: '',
    password: '',
  });
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [showVerificationSuccess, setShowVerificationSuccess] = React.useState(false);
  const stateReturnRoute = React.useMemo(
    () => getReturnRouteFromState((location.state as { from?: unknown } | null)?.from),
    [location.state]
  );
  const redirectTo = React.useMemo(() => {
    if (stateReturnRoute) return toPathString(stateReturnRoute);
    return getDashboardFallback();
  }, [stateReturnRoute]);
  const returnLabel = React.useMemo(
    () => (stateReturnRoute ? deriveLabel(stateReturnRoute) : 'Dashboard'),
    [stateReturnRoute]
  );

  React.useEffect(() => {
    if (stateReturnRoute) {
      saveAuthReturnRoute(stateReturnRoute);
    }
  }, [stateReturnRoute]);

  React.useEffect(() => {
    // Check if user was redirected from email verification
    const verified = searchParams.get('verified');
    if (verified === 'true') {
      setShowVerificationSuccess(true);
      searchParams.delete('verified');
      const nextSearch = searchParams.toString();
      navigate(
        {
          pathname: '/login',
          search: nextSearch ? `?${nextSearch}` : '',
        },
        { replace: true, state: location.state }
      );
    }
  }, [searchParams, navigate, location.state]);

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate(getDashboardFallback(), { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      await login(formData.email, formData.password);
      navigate(getDashboardFallback(), { replace: true });
    } catch (err: any) {
      setError(err.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          borderRadius: 4,
          position: 'relative',
          overflow: 'hidden',
          border: isDark ? '1px solid transparent' : `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          background: isDark
            ? `
              linear-gradient(135deg, rgba(2, 6, 23, 0.92) 0%, rgba(17, 24, 39, 0.92) 100%) padding-box,
              linear-gradient(135deg, rgba(34, 197, 94, 0.55), rgba(59, 130, 246, 0.35), rgba(236, 72, 153, 0.22)) border-box
            `
            : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          backdropFilter: isDark ? 'blur(14px)' : undefined,
          boxShadow: isDark
            ? '0 22px 80px rgba(0,0,0,0.75), 0 0 0 1px rgba(255,255,255,0.06), 0 0 60px rgba(34,197,94,0.10), 0 0 70px rgba(59,130,246,0.10)'
            : theme.shadows[3],
          '&:before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: isDark
              ? `
                radial-gradient(900px circle at 18% 12%, rgba(34,197,94,0.18), transparent 55%),
                radial-gradient(800px circle at 85% 20%, rgba(59,130,246,0.16), transparent 55%),
                radial-gradient(700px circle at 50% 110%, rgba(236,72,153,0.10), transparent 60%)
              `
              : 'none',
            pointerEvents: 'none',
            zIndex: 0,
          },
          '&:after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: isDark
              ? 'repeating-linear-gradient(0deg, rgba(255,255,255,0.035) 0, rgba(255,255,255,0.035) 1px, transparent 1px, transparent 4px)'
              : 'none',
            opacity: isDark ? 0.10 : 0,
            pointerEvents: 'none',
            zIndex: 0,
            mixBlendMode: 'overlay',
          },
          '& > *': { position: 'relative', zIndex: 1 },
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Avatar 
            sx={{ 
              width: 80, 
              height: 80, 
              mx: 'auto', 
              mb: 2, 
              bgcolor: 'primary.main',
              border: isDark ? `1px solid ${alpha('#ffffff', 0.12)}` : undefined,
              boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.55), 0 0 30px rgba(34,197,94,0.20)' : 3,
            }}
          >
            <LoginIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Welcome Back
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Sign in to continue where you left off
          </Typography>
          {stateReturnRoute && (
            <Box sx={{ mt: 2 }}>
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={`Returning to ${returnLabel} after sign in`}
              />
            </Box>
          )}
        </Box>

        {/* Email Verification Success Message */}
        {showVerificationSuccess && (
          <Card sx={{ mb: 3, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <CheckCircle color="success" />
                <Typography variant="h6" color="success.main">
                  Email Verified Successfully!
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Your email has been verified. You can now log in to your account and start using FarmersHub.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                <Chip label="Smart Farming" size="small" />
                <Chip label="Marketplace" size="small" />
                <Chip label="Community" size="small" />
                <Chip label="Analytics" size="small" />
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="body1" fontWeight="bold">
              Login Failed
            </Typography>
            <Typography variant="body2">
              {error}
            </Typography>
          </Alert>
        )}

        {/* Login Form */}
        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            margin="normal"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
            autoComplete="email"
            InputProps={{
              startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                bgcolor: isDark ? alpha('#ffffff', 0.06) : alpha('#ffffff', 0.5),
              },
              '& .MuiInputBase-input': {
                color: theme.palette.text.primary,
                caretColor: theme.palette.text.primary,
              },
              '& .MuiInputLabel-root': {
                color: theme.palette.text.secondary,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.text.primary, isDark ? 0.18 : 0.15),
              },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
              '& .MuiOutlinedInput-root.Mui-focused': {
                boxShadow: isDark ? '0 0 0 4px rgba(34,197,94,0.14)' : '0 0 0 4px rgba(34,197,94,0.10)',
              },
            }}
          />
          <TextField
            fullWidth
            label="Password"
            type="password"
            margin="normal"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            required
            autoComplete="current-password"
            InputProps={{
              startAdornment: <Lock sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                bgcolor: isDark ? alpha('#ffffff', 0.06) : alpha('#ffffff', 0.5),
              },
              '& .MuiInputBase-input': {
                color: theme.palette.text.primary,
                caretColor: theme.palette.text.primary,
              },
              '& .MuiInputLabel-root': {
                color: theme.palette.text.secondary,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.text.primary, isDark ? 0.18 : 0.15),
              },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
              '& .MuiOutlinedInput-root.Mui-focused': {
                boxShadow: isDark ? '0 0 0 4px rgba(34,197,94,0.14)' : '0 0 0 4px rgba(34,197,94,0.10)',
              },
            }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            disabled={loading}
            sx={{ 
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 'bold',
              mb: 3,
              borderRadius: 2,
              background: isDark
                ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 40%, #14b8a6 100%)'
                : undefined,
              boxShadow: isDark ? '0 14px 40px rgba(0,0,0,0.55), 0 0 28px rgba(34,197,94,0.18)' : undefined,
              '&:hover': {
                background: isDark
                  ? 'linear-gradient(135deg, #16a34a 0%, #22c55e 45%, #06b6d4 100%)'
                  : undefined,
                boxShadow: isDark ? '0 18px 55px rgba(0,0,0,0.6), 0 0 38px rgba(34,197,94,0.22)' : undefined,
              },
            }}
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </Button>

          {/* OAuth Buttons */}
          <Box sx={{ position: 'relative', mb: 4 }}>
            <Divider sx={{ mb: 4 }}>
              <Typography variant="body2" color="text.secondary">
                OR CONTINUE WITH
              </Typography>
            </Divider>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={
                    <svg width="18" height="18" viewBox="0 0 24 24">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                    </svg>
                  }
                  onClick={() => signInWithProvider('google')}
                  disabled={loading}
                  sx={{ py: 1, textTransform: 'none', fontWeight: 'bold', fontSize: '0.85rem' }}
                >
                  Google
                </Button>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={
                    <svg width="18" height="18" viewBox="0 0 24 24">
                      <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" fill="currentColor" />
                    </svg>
                  }
                  onClick={() => signInWithProvider('github')}
                  disabled={loading}
                  sx={{ py: 1, textTransform: 'none', fontWeight: 'bold', fontSize: '0.85rem' }}
                >
                  GitHub
                </Button>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={
                    <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.856.925-1.856 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                  }
                  onClick={() => signInWithProvider('facebook')}
                  disabled={loading}
                  sx={{ py: 1, textTransform: 'none', fontWeight: 'bold', fontSize: '0.85rem', color: '#1877F2', borderColor: '#1877F2' }}
                >
                  Facebook
                </Button>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={
                    <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                  }
                  onClick={() => signInWithProvider('linkedin_oidc')}
                  disabled={loading}
                  sx={{ py: 1, textTransform: 'none', fontWeight: 'bold', fontSize: '0.85rem', color: '#0077B5', borderColor: '#0077B5' }}
                >
                  LinkedIn
                </Button>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={
                    <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037 19.736 19.736 0 0 0-4.885 1.515.069.07 0 0 0-.032.027C.533 9.048-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
                    </svg>
                  }
                  onClick={() => signInWithProvider('discord')}
                  disabled={loading}
                  sx={{ py: 1, textTransform: 'none', fontWeight: 'bold', fontSize: '0.85rem', color: '#5865F2', borderColor: '#5865F2' }}
                >
                  Discord
                </Button>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={
                    <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 7.719 8.502 11.231h-6.657l-5.213-6.817-5.966 6.817H1.639l7.71-8.232L1.22 2.25h6.826l4.704 6.157 5.494-6.157zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
                    </svg>
                  }
                  onClick={() => signInWithProvider('twitter')}
                  disabled={loading}
                  sx={{ py: 1, textTransform: 'none', fontWeight: 'bold', fontSize: '0.85rem', color: '#000', borderColor: '#000' }}
                >
                  X
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>

        {/* Footer Links */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" paragraph>
            Don't have an account?{' '}
            <Link 
              component="button"
              type="button"
              underline="hover" 
              sx={{ fontWeight: 'bold' }}
              onClick={() => navigate('/register', { state: location.state })}
            >
              Create one here
            </Link>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <Link href="/forgot-password" underline="hover">
              Forgot your password?
            </Link>
          </Typography>
        </Box>

        {/* Demo Account Info */}
        <Box sx={{ mt: 4, p: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            <strong>Demo Account:</strong> free@demo.com / password123
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default Login; 
